/**
 * Páginas de sistema del Portal (brand book SOA 10 PRO · §14, variante cliente): misma plantilla que
 * el OS pero con tono para el cliente — frases breves, sin códigos ni palabras técnicas. El ícono va en
 * un cuadro #CFFAFE con trazo #0E7490 (el rojo del portal ya no se usa).
 */
import { useEffect, useState } from "react";

// Íconos de las páginas de sistema como SVG propio: estas pantallas (mantenimiento, error, sin conexión) aparecen justo
// cuando algo falla, y no pueden depender de que cargue una fuente externa (se veía la palabra «build»).
const RUTAS = {
  build: "M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z",
  error: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z",
  search: "M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
  refresh: "M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z",
  mail: "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z",
};
function IconoSistema({ nombre, size = 30 }) {
  return <svg aria-hidden="true" width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d={RUTAS[nombre] || RUTAS.error} /></svg>;
}

function Marco({ icono = "refresh", titulo, texto, primario, secundario, pie }) {
  const boton = (b, principal) => b && (
    b.href
      ? <a key={b.label} href={b.href} className="inline-flex items-center justify-center h-[50px] px-6 rounded-[15px] text-[15px] font-extrabold no-underline"
           style={{ background: principal ? "#111318" : "transparent", color: principal ? "#FFFFFF" : "#111318", boxShadow: principal ? "none" : "inset 0 0 0 2px #CDD0D7" }}>{b.label}</a>
      : <button key={b.label} type="button" onClick={b.onClick} className="inline-flex items-center justify-center h-[50px] px-6 rounded-[15px] text-[15px] font-extrabold cursor-pointer border-0"
                style={{ background: principal ? "#111318" : "transparent", color: principal ? "#FFFFFF" : "#111318", boxShadow: principal ? "none" : "inset 0 0 0 2px #CDD0D7" }}>{b.label}</button>
  );
  return (
    <div role="main" className="min-h-screen flex items-center justify-center p-6" style={{ background: "#F2F3F6", color: "#111318", fontFamily: "Figtree, sans-serif" }}>
      <div className="w-full max-w-[520px]">
        <div aria-hidden="true" className="grid place-items-center w-[60px] h-[60px] rounded-[18px]" style={{ background: "#CFFAFE", color: "#0E7490" }}>
          <IconoSistema nombre={icono} size={30} />
        </div>
        <h1 className="mt-7" style={{ fontWeight: 800, fontSize: 28, lineHeight: 1.05, letterSpacing: "-0.02em", textWrap: "balance" }}>{titulo}</h1>
        {texto && <p className="mt-4 text-[18px] leading-relaxed" style={{ color: "#4B4F59" }}>{texto}</p>}
        {(primario || secundario) && <div className="mt-7 flex flex-wrap gap-3">{boton(primario, true)}{boton(secundario, false)}</div>}
        {pie && <p className="mt-5 text-[14px]" style={{ color: "#4B4F59" }}>{pie}</p>}
      </div>
    </div>
  );
}

const recargar = () => window.location.reload();

/** Cuándo: la API del portal responde 503 con codigo "mantenimiento" (PORTAL_CERRADO). Reintenta sola cada 60 s. */
export function PortalMantenimiento({ vuelve = "23:30" }) {
  useEffect(() => { const t = setTimeout(recargar, 60_000); return () => clearTimeout(t); }, []);
  return (
    <Marco icono="build" titulo="Estamos mejorando tu portal"
           texto="Volvemos en un rato con una versión nueva. Tus piezas, aprobaciones y reportes están guardados."
           primario={{ label: "Reintentar", onClick: recargar }}
           pie={`Disponible antes de las ${vuelve}. ¿Algo urgente? Escribile a tu ejecutiva por mail o WhatsApp.`} />
  );
}

/** Cuándo: error de render no controlado. */
export function PortalError({ onReintentar = recargar }) {
  return <Marco icono="error" titulo="Algo no anduvo" texto="Ya avisamos al equipo. Probá de nuevo en unos segundos." primario={{ label: "Reintentar", onClick: onReintentar }} secundario={{ label: "Ir al inicio", href: "/" }} />;
}

/** Cuándo: la dirección no existe en el portal. */
export function Portal404() {
  return <Marco icono="search" titulo="No encontramos esa página" texto="Puede que el enlace esté viejo. Volvé al inicio y buscala desde ahí." primario={{ label: "Ir al inicio", href: "/" }} />;
}

/** Cuándo: el navegador pierde la conexión. */
export function PortalSinConexion() {
  return <Marco icono="refresh" titulo="Sin conexión" texto="Cuando vuelva la señal seguimos. No se pierde nada de lo que ya estaba guardado." primario={{ label: "Reintentar", onClick: recargar }} />;
}

/** Cuándo: el link de acceso (VerifyMagic / compartido) venció. */
export function PortalLinkVencido() {
  return <Marco icono="mail" titulo="Este link ya no sirve" texto="Los links de acceso duran poco por seguridad. Pedí uno nuevo y entrás al toque." primario={{ label: "Pedir un link nuevo", href: "/login" }} />;
}

/** Aviso cuando se pierde la conexión mientras se usa el portal (no tapa la pantalla). */
export function AvisoSinConexion() {
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  useEffect(() => {
    const on = () => setOffline(false), off = () => setOffline(true);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (!offline) return null;
  return (
    <div role="status" className="fixed left-1/2 -translate-x-1/2 bottom-[88px] sm:bottom-5 z-[var(--z-notice)] px-4 py-2.5 rounded-[16px] text-[14px] font-bold shadow-[0_4px_16px_rgba(17,19,24,0.14)]"
         style={{ background: "#111318", color: "#FFFFFF" }}>
      Sin conexión · mostramos lo último que guardamos
    </div>
  );
}

/** Pantalla completa de mantenimiento: escucha el evento que dispara lib/api.js ante un 503 de mantenimiento. */
export function PortalMantenimientoGate() {
  const [estado, setEstado] = useState(null);
  useEffect(() => {
    const on = (e) => setEstado({ vuelve: e?.detail?.vuelve || "23:30" });
    window.addEventListener("portal-mantenimiento", on);
    return () => window.removeEventListener("portal-mantenimiento", on);
  }, []);
  if (!estado) return null;
  return <div role="alertdialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 10000 }}><PortalMantenimiento vuelve={estado.vuelve} /></div>;
}
